import { Injectable } from '@angular/core'

/** Estado da conexão de um cliente STOMP, como os serviços de socket o reportam. */
export type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected'

/**
 * Canais de tempo real que a aplicação acompanha. A chave identifica o CANAL, não a conexão: dois
 * componentes podem assinar o mesmo canal, e o que interessa é se ele está entregando.
 */
export type RealtimeChannel = 'phase' | 'bid' | 'session' | 'judgment' | 'chat' | 'notification' | 'pagination' | 'book'

/**
 * Saúde do tempo real, por canal: status da conexão e instante do último frame.
 *
 * O status é o que decide o fallback — o socket é o canal primário, e o poller de escape só busca
 * por REST enquanto o canal não está `connected` (`RealtimeFallbackPoller`).
 *
 * O carimbo do último frame fica como diagnóstico. Nasceu como régua do poller na época do broker
 * em memória por pod, em que um socket conectado podia estar mudo; com o relay para o RabbitMQ esse
 * modo de falha acabou, e decidir polling por silêncio virou carga à toa (numa sessão sem lances
 * silêncio é o normal). Não o use para disparar busca.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeHealthService {
	private readonly statusByChannel = new Map<RealtimeChannel, RealtimeConnectionStatus>()
	private readonly lastFrameAtByChannel = new Map<RealtimeChannel, number>()

	/** Chamado pelo serviço de socket a cada frame recebido. */
	markFrame(channel: RealtimeChannel): void {
		this.lastFrameAtByChannel.set(channel, Date.now())
	}

	markStatus(channel: RealtimeChannel, status: RealtimeConnectionStatus): void {
		this.statusByChannel.set(channel, status)

		// Um frame anterior à queda não diz nada sobre o canal depois dela.
		if (status !== 'connected') this.lastFrameAtByChannel.delete(channel)
	}

	status(channel: RealtimeChannel): RealtimeConnectionStatus {
		return this.statusByChannel.get(channel) ?? 'disconnected'
	}

	/** Instante do último frame recebido no canal, ou null se nunca recebeu (ou caiu desde então). */
	lastFrameAt(channel: RealtimeChannel): number | null {
		return this.lastFrameAtByChannel.get(channel) ?? null
	}

	/** Todos os canais informados estão conectados? */
	allConnected(channels: RealtimeChannel[]): boolean {
		return (channels ?? []).every(channel => this.status(channel) === 'connected')
	}

	/** Usado ao sair da tela/sessão, para o estado de um canal não vazar para a próxima. */
	reset(channel: RealtimeChannel): void {
		this.statusByChannel.delete(channel)
		this.lastFrameAtByChannel.delete(channel)
	}
}
